/**
 * TaskDrawerSidebar Component
 *
 * Renders the right sidebar containing:
 * - Followers card
 * - Details/metadata card
 * - Assignees and collaborators card
 *
 * Layout adapts responsively:
 * - Desktop: Fixed width sidebar
 * - Tablet/Mobile: Full width stacked cards
 *
 * Accessibility:
 * - Landmark region for sidebar navigation
 * - Proper heading hierarchy within cards
 * - Keyboard navigable interactive elements
 *
 * @see WCAG 2.1 AA - 1.3.1 Info and Relationships
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Select, Tooltip, message } from 'antd';
import { CopyOutlined, EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import TaskDrawerFollowers from './TaskDrawerFollowers';
import TaskDrawerDetails from './TaskDrawerDetails';
import TaskDrawerAssignees from './TaskDrawerAssignees';
import { copyTaskLink, isRestrictedTaskLevel } from '../../utils/task.utils';
import { statusTypes } from '../../configs/task.config';
import '../../styles/index.scss';

const { Option } = Select;

const TaskDrawerSidebar = ({
  currentTask,
  taskId,
  currentUser,
  form,
  level,
  // State
  isEditingAssignee,
  setIsEditingAssignee,
  isFollowers,
  isCollaborator,
  isCurrentUserAssignee,
  // Loading states
  updateTaskLoading,
  // Actions
  updateTask,
  fetchTask,
  onTaskUpdate,
  executeWithUnsavedCheck,
  onStatusChange,
  // User/Org data fetching
  fetchBasicOrgs,
  fetchBasicUsers,
  fetchCollaborators,
  basicOrgsDetails,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  collaboratorsData,
  collaboratorsLoading,
  // Inline details editing
  isEditingDetails,
  setIsEditingDetails,
  detailsForm,
  onDetailsFieldChange,
  onDetailsSave,
  onDetailsCancel,
  channelPartnerOrgs,
  advertiserOrgs,
  channelPartnerOrgsLoading,
  advertiserOrgsLoading,
  fetchChannelPartnerOrgs,
  fetchAdvertiserOrgs,
}) => {
  const handleCopyLink = () => {
    copyTaskLink(taskId, message);
  };

  return (
    <aside
      className="task-drawer-full__sidebar"
      role="complementary"
      aria-label="Task metadata and actions"
    >
      {/* Actions Bar */}
      <div className="task-drawer-full__sidebar-actions" role="toolbar" aria-label="Task actions">
        {!isRestrictedTaskLevel(level) && (
          <Select
            className="custom-select task-drawer-full__status-select"
            value={currentTask?.status}
            placeholder="Update Status"
            onChange={onStatusChange}
            loading={updateTaskLoading}
            disabled={updateTaskLoading}
            aria-label={`Task status: ${currentTask?.status || 'Not set'}. Select to change status.`}
          >
            {Object.keys(statusTypes).map((status) => (
              <Option key={statusTypes[status]} value={statusTypes[status]}>
                {status}
              </Option>
            ))}
          </Select>
        )}
        <Tooltip title="Copy Task Link">
          <Button
            onClick={handleCopyLink}
            icon={<CopyOutlined />}
            className="task-drawer-full__copy-btn"
            aria-label="Copy task link to clipboard"
          />
        </Tooltip>
        <Tooltip title="Edit Task">
          <Link to={`/tasks/form/${taskId}`}>
            <Button
              icon={<EditOutlined />}
              className="task-drawer-full__edit-btn"
              aria-label={`Edit task: ${currentTask?.name}`}
            />
          </Link>
        </Tooltip>
      </div>

      {/* Followers Card */}
      <TaskDrawerFollowers
        currentTask={currentTask}
        taskId={taskId}
        currentUser={currentUser}
        isFollowers={isFollowers}
        isCurrentUserAssignee={isCurrentUserAssignee}
        isCollaborator={isCollaborator}
        updateTaskLoading={updateTaskLoading}
        updateTask={updateTask}
        fetchTask={fetchTask}
        onTaskUpdate={onTaskUpdate}
        executeWithUnsavedCheck={executeWithUnsavedCheck}
      />

      {/* Details Card */}
      <TaskDrawerDetails
        currentTask={currentTask}
        level={level}
        updateTaskLoading={updateTaskLoading}
        isEditingDetails={isEditingDetails}
        setIsEditingDetails={setIsEditingDetails}
        detailsForm={detailsForm}
        onDetailsFieldChange={onDetailsFieldChange}
        onDetailsSave={onDetailsSave}
        onDetailsCancel={onDetailsCancel}
        channelPartnerOrgs={channelPartnerOrgs}
        advertiserOrgs={advertiserOrgs}
        channelPartnerOrgsLoading={channelPartnerOrgsLoading}
        advertiserOrgsLoading={advertiserOrgsLoading}
        fetchChannelPartnerOrgs={fetchChannelPartnerOrgs}
        fetchAdvertiserOrgs={fetchAdvertiserOrgs}
      />

      {/* Assignees & Collaborators Card */}
      <TaskDrawerAssignees
        currentTask={currentTask}
        taskId={taskId}
        currentUser={currentUser}
        form={form}
        level={level}
        isEditingAssignee={isEditingAssignee}
        setIsEditingAssignee={setIsEditingAssignee}
        isCollaborator={isCollaborator}
        isCurrentUserAssignee={isCurrentUserAssignee}
        isFollowers={isFollowers}
        updateTaskLoading={updateTaskLoading}
        updateTask={updateTask}
        fetchTask={fetchTask}
        onTaskUpdate={onTaskUpdate}
        executeWithUnsavedCheck={executeWithUnsavedCheck}
        fetchBasicOrgs={fetchBasicOrgs}
        fetchBasicUsers={fetchBasicUsers}
        fetchCollaborators={fetchCollaborators}
        basicOrgsDetails={basicOrgsDetails}
        basicOrgsDetailsLoading={basicOrgsDetailsLoading}
        basicUsersDetails={basicUsersDetails}
        basicUsersDetailsLoading={basicUsersDetailsLoading}
        collaboratorsData={collaboratorsData}
        collaboratorsLoading={collaboratorsLoading}
      />
    </aside>
  );
};

TaskDrawerSidebar.propTypes = {
  currentTask: PropTypes.object,
  taskId: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.object.isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool,
  setIsEditingAssignee: PropTypes.func.isRequired,
  isFollowers: PropTypes.bool,
  isCollaborator: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  updateTask: PropTypes.func.isRequired,
  fetchTask: PropTypes.func.isRequired,
  onTaskUpdate: PropTypes.func,
  executeWithUnsavedCheck: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func,
  fetchBasicOrgs: PropTypes.func.isRequired,
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  collaboratorsData: PropTypes.array,
  collaboratorsLoading: PropTypes.bool,
  isEditingDetails: PropTypes.bool,
  setIsEditingDetails: PropTypes.func,
  detailsForm: PropTypes.object,
  onDetailsFieldChange: PropTypes.func,
  onDetailsSave: PropTypes.func,
  onDetailsCancel: PropTypes.func,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  channelPartnerOrgsLoading: PropTypes.bool,
  advertiserOrgsLoading: PropTypes.bool,
  fetchChannelPartnerOrgs: PropTypes.func,
  fetchAdvertiserOrgs: PropTypes.func,
};

TaskDrawerSidebar.defaultProps = {
  currentTask: null,
  level: null,
  isEditingAssignee: false,
  isFollowers: false,
  isCollaborator: false,
  isCurrentUserAssignee: false,
  updateTaskLoading: false,
  onTaskUpdate: null,
  onStatusChange: null,
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  collaboratorsData: [],
  collaboratorsLoading: false,
  isEditingDetails: false,
  setIsEditingDetails: () => {},
  detailsForm: {},
  onDetailsFieldChange: () => {},
  onDetailsSave: () => {},
  onDetailsCancel: () => {},
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  channelPartnerOrgsLoading: false,
  advertiserOrgsLoading: false,
  fetchChannelPartnerOrgs: () => {},
  fetchAdvertiserOrgs: () => {},
};

export default TaskDrawerSidebar;
